// "Consumer Voice History" list under the Catalog > Brands table (and the
// internal Brands page): network-scoped runs from GET /rmn/consumer-voice/
// reports, polled while any is pending. Sibling of ReportHistorySection.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from '@mui/material';
import { FileDownloadOutlined, VisibilityOutlined } from '@mui/icons-material';
import dayjs from 'dayjs';
import axiosInstant from '../../api';
import { DataTable, DataTableColumn } from '../DataTable/DataTable';
import { ConsumerVoiceAudit, cvSubjectLabel, isCvRunning } from '../../types/consumerVoice';
import { CvScoreChip } from './CvScoreChip';
import { ConsumerVoiceReportDialog } from './ConsumerVoiceReportDialog';
import { downloadConsumerVoicePdf } from './consumerVoiceFiles';
import { ConsumerVoiceScoreChart } from './ConsumerVoiceScoreChart';

const POLL_INTERVAL_MS = 8000;
const PAGE_SIZE = 20;

export function ConsumerVoiceHistorySection({
  listEndpoint,
  itemEndpoint,
  title = 'Consumer Voice History',
  refreshKey = 0,
  onRowsChange,
  onCreateStrategy,
  kind,
  showChart = false,
  emptyMessage = 'No Consumer Voice analyses yet — run one from a brand or product row.',
}: {
  // GET list ({rows, count} or an array) and GET one (with report).
  listEndpoint: string;
  itemEndpoint: (id: number) => string;
  title?: string;
  refreshKey?: number;
  onRowsChange?: (rows: ConsumerVoiceAudit[]) => void;
  onCreateStrategy?: (audit: ConsumerVoiceAudit) => void;
  kind?: 'product' | 'brand';
  // Score-over-time chart above the table (one subject's runs — the product
  // settings tab). Off for mixed-subject lists, where a line means nothing.
  showChart?: boolean;
  // Single brands (free / standard / enterprise) have no brand rows to run
  // from, so their mounts pass product-only copy.
  emptyMessage?: string;
}) {
  const [rows, setRows] = useState<ConsumerVoiceAudit[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const onRowsChangeRef = useRef(onRowsChange);
  onRowsChangeRef.current = onRowsChange;

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const sep = listEndpoint.includes('?') ? '&' : '?';
      const res = await axiosInstant.get(`${listEndpoint}${sep}limit=${PAGE_SIZE}${kind ? `&kind=${kind}` : ''}`);
      const { data } = res;
      const list: ConsumerVoiceAudit[] = Array.isArray(data) ? data : data.rows || [];
      setRows(list);
      setCount(Array.isArray(data) ? list.length : data.count || list.length);
      setError(null);
      onRowsChangeRef.current?.(list);
    } catch {
      setError("Couldn't load Consumer Voice history.");
    } finally {
      setLoading(false);
    }
  }, [listEndpoint, kind]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const anyRunning = rows.some(isCvRunning);
  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => load(true), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [anyRunning, load]);

  const columns: DataTableColumn<ConsumerVoiceAudit>[] = [
    {
      key: 'subject',
      header: 'Subject',
      render: (r) => (
        <div className="min-w-0">
          <div className="font-semibold text-text-1 truncate max-w-[320px]" title={cvSubjectLabel(r)}>{cvSubjectLabel(r)}</div>
          <div className="text-[11px] text-muted">
            {r.kind === 'brand'
              ? `Brand · ${r.params.product_count ? `${r.params.product_count} products` : 'products'}`
              : `Product${r.params.brand_name ? ` · ${r.params.brand_name}` : ''}`}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        if (r.status === 'ready') {
          return <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#2E7D32] bg-[#E8F5E9] rounded-full px-2.5 py-1">Ready</span>;
        }
        if (isCvRunning(r)) {
          return (
            <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-accent-text bg-tint rounded-full px-2.5 py-1">
              Running{r.params.progress ? ` ${r.params.progress.done}/${r.params.progress.total}` : '…'}
            </span>
          );
        }
        return (
          <Tooltip title={r.generation_error || 'Failed'}>
            <span className="text-[11px] font-bold uppercase tracking-[0.5px] text-[#c62828] bg-[#FBEFEF] rounded-full px-2.5 py-1">Failed</span>
          </Tooltip>
        );
      },
    },
    {
      key: 'score',
      header: 'CV Score',
      render: (r) => (r.status === 'ready' ? <CvScoreChip score={r.overall_score} subscores={r.subscores} /> : <span className="text-muted">—</span>),
    },
    {
      key: 'created',
      header: 'Created',
      render: (r) => <span className="text-[12px] text-text-2 whitespace-nowrap">{dayjs(r.created_at).format('MMM DD, YYYY h:mm A')}</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Tooltip title="View">
            <span>
              <button
                type="button"
                aria-label="View analysis"
                disabled={r.status !== 'ready'}
                onClick={() => setViewingId(r.id)}
                className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <VisibilityOutlined sx={{ fontSize: 15 }} />
              </button>
            </span>
          </Tooltip>
          <Tooltip title="Download PDF">
            <span>
              <button
                type="button"
                aria-label="Download analysis"
                disabled={r.status !== 'ready'}
                onClick={() => downloadConsumerVoicePdf(itemEndpoint(r.id))}
                className="w-6 h-6 flex items-center justify-center rounded text-muted hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FileDownloadOutlined sx={{ fontSize: 15 }} />
              </button>
            </span>
          </Tooltip>
          {onCreateStrategy && (
            <button
              type="button"
              title={r.status === 'ready' ? 'Start a strategy from this analysis' : 'Available once the analysis is ready'}
              disabled={r.status !== 'ready'}
              onClick={() => onCreateStrategy(r)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-border-token rounded-md bg-surface text-[11.5px] font-medium text-accent-text cursor-pointer hover:border-accent hover:bg-tint disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
            >
              Create strategy
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-[16px] font-bold text-text-1">{title}</h2>
        {count > 0 && <span className="text-[12px] text-muted">{count} total</span>}
      </div>
      {showChart && <ConsumerVoiceScoreChart rows={rows} />}
      <DataTable<ConsumerVoiceAudit>
        columns={columns}
        rows={rows}
        loading={loading}
        totalCount={count}
        hasMore={false}
        error={error}
        onLoadMore={() => undefined}
        emptyMessage={emptyMessage}
        getRowKey={(r) => r.id}
      />
      {viewingId !== null && (
        <ConsumerVoiceReportDialog
          endpoint={itemEndpoint(viewingId)}
          productEndpoint={itemEndpoint}
          onClose={() => setViewingId(null)}
          onCreateStrategy={onCreateStrategy}
          createStrategyLabel="Create strategy"
        />
      )}
    </div>
  );
}
